import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Send, 
  Users, 
  Mail, 
  FileText, 
  CheckCircle, 
  XCircle,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DashboardPage = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Emails Sent',
      value: stats?.total_emails || 0,
      icon: Send,
      color: 'bg-primary',
      show: true
    },
    {
      title: 'Successful',
      value: stats?.sent_emails || 0,
      icon: CheckCircle,
      color: 'bg-emerald-500',
      show: true
    },
    {
      title: 'Failed',
      value: stats?.failed_emails || 0,
      icon: XCircle,
      color: 'bg-red-500',
      show: true
    },
    {
      title: 'Active Templates',
      value: stats?.total_templates || 0,
      icon: FileText,
      color: 'bg-blue-500',
      show: true
    },
    {
      title: 'Active Senders',
      value: stats?.total_senders || 0,
      icon: Mail,
      color: 'bg-violet-500',
      show: true
    },
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'bg-amber-500',
      show: isAdmin()
    }
  ];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {getGreeting()}, {user?.name}
        </h1>
        <p className="text-slate-500 mt-1">
          Here's what's happening with your email campaigns
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {loading ? (
          // Skeleton Loading
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="border-slate-200">
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-10 w-20" />
              </CardContent>
            </Card>
          ))
        ) : (
          statCards.filter(card => card.show).map((card, index) => (
            <Card 
              key={index} 
              className="border-slate-200 hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
              data-testid={`stat-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-2">{card.title}</p>
                    <p className="text-4xl font-bold text-slate-900">{card.value}</p>
                  </div>
                  <div className={`${card.color} p-3 rounded-xl`}>
                    <card.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Quick Actions */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <TrendingUp className="w-5 h-5 text-primary" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a
              href="/send-email"
              className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all group"
              data-testid="quick-action-send-email"
            >
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Send className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Send Email</p>
                <p className="text-sm text-slate-500">Compose and send</p>
              </div>
            </a>

            <a
              href="/history"
              className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all group"
              data-testid="quick-action-view-history"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">View History</p>
                <p className="text-sm text-slate-500">See past emails</p>
              </div>
            </a>

            {user?.role !== 'agent' && (
              <>
                <a
                  href="/templates"
                  className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-violet-500 hover:bg-violet-50 transition-all group"
                  data-testid="quick-action-templates"
                >
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center group-hover:bg-violet-200 transition-colors">
                    <FileText className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Templates</p>
                    <p className="text-sm text-slate-500">Manage templates</p>
                  </div>
                </a>

                <a
                  href="/senders"
                  className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all group"
                  data-testid="quick-action-senders"
                >
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <Users className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Senders</p>
                    <p className="text-sm text-slate-500">Manage senders</p>
                  </div>
                </a>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
